/**
 * D: "Plan & billing" for individual plans on web (settings dialog pane).
 *
 * Quiet summary card (tier, tagline, Nova, price + renewal line), a text-first
 * activity strip from real server counts (hidden when nothing is countable,
 * never fabricated), two action rows, "Plan details", "Billing help" and the
 * public "Rate Tabbio" links. Management/cancel go through the multi-step
 * journey dialog, which only hands off to the billing provider.
 *
 * Web variant: "Refresh subscription" (a sync) instead of "Restore purchases",
 * except for App Store / Google Play-owned plans, whose row keeps the Restore
 * label (on web it is the same sync: purchases live with the store).
 */
import { useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { normalizeLegacyPlanTier } from '@tabbio/utils/subscription/catalog';
import { useAuth } from '../../hooks/useAuth';
import { useLocale } from '../../hooks/useLocale';
import { useToast } from '../../hooks/useToast';
import { useRevenueCat } from '../../hooks/useRevenueCat';
import { useSubscriptionState } from '../../hooks/useSubscriptionState';
import { useSubscriptionActivity } from '../../hooks/useSubscriptionActivity';
import { useUpgradeModal } from '../../providers/useUpgradeModal';
import { useDialog } from '../../providers/DialogProvider';
import { useSubscriptionDisplay } from '../../lib/subscription-display';
import { buildPublicProfileUrl } from '../../lib/public-web';
import { withOwnerViewMarker } from '@tabbio/utils/analytics/cv-traffic';
import { trackProductEvent } from '../../lib/product-analytics';
import {
  canStartCancellation,
  formatCancelAccessDate,
} from '../../lib/subscription-journey/cancel-journey';
import { formatActivityCount } from '../../lib/subscription-journey/activity-metrics';
import { getTierPrices } from '../../lib/subscription-journey/pricing';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { NovaMood } from '../subscription/NovaMood.web';
import { PremiumMark } from '../subscription/PremiumMark.web';
import { TABBIO_CV_MARK, toAssetSrc } from '../subscription/nova-assets';
import { IMPROVE_CV_HREF } from '../subscription/WelcomeContent.web';
import {
  Icon,
  InfoDialog,
  SectionTitle,
  SubGroup,
  SubRow,
  storeProviderKey,
} from '../subscription/journey-ui.web';
import { CancelPlanDialog, type CancelJourneyEntry } from './CancelPlanDialog.web';

const TIER_NAMES = { plus: 'Plus', pro: 'Pro' } as const;
const CV_MARK_SRC = toAssetSrc(TABBIO_CV_MARK);

type InfoKey = 'nextPayment' | 'receipts' | 'refund' | null;

function CvGlyph() {
  return (
    <span
      aria-hidden
      style={{
        display: 'block',
        width: 22,
        height: 27,
        backgroundColor: 'var(--sub-brand)',
        WebkitMaskImage: `url(${CV_MARK_SRC})`,
        maskImage: `url(${CV_MARK_SRC})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
      }}
    />
  );
}

export function SubscriptionBillingPanel() {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const { isRTL } = useLocale();
  const router = useRouter();
  const toast = useToast();
  const { user } = useAuth();
  const { catalog, snapshot, isLoading } = useSubscriptionState();
  const display = useSubscriptionDisplay('web');
  const { syncSubscription } = useRevenueCat();
  const upgradeModal = useUpgradeModal();
  const { close: closeActiveDialog } = useDialog();
  const [journey, setJourney] = useState<CancelJourneyEntry | null>(null);
  const [info, setInfo] = useState<InfoKey>(null);
  const [refreshing, setRefreshing] = useState(false);

  const tier = normalizeLegacyPlanTier(snapshot?.tier ?? 'free');
  const paid = tier === 'plus' || tier === 'pro';
  const activity = useSubscriptionActivity({ enabled: Boolean(snapshot) });
  const provider = t(storeProviderKey(snapshot?.store));
  const storeOwned = snapshot?.store === 'app_store' || snapshot?.store === 'play_store';
  const cycle = snapshot?.billingInterval === 'yearly' ? 'yearly' : 'monthly';
  const prices = paid ? getTierPrices(catalog, 'individual', tier) : null;
  const priceDisplay = prices
    ? cycle === 'yearly'
      ? prices.yearlyTotalDisplay
      : prices.monthlyDisplay
    : null;
  const renewalOff = paid && snapshot?.willRenew === false;
  // The provider's date is the only date; a past date is not a promise.
  const renewsAtIso =
    snapshot?.renewsAt && Date.parse(snapshot.renewsAt) > Date.now()
      ? snapshot.renewsAt
      : null;
  const date = formatCancelAccessDate(renewsAtIso, locale);
  const hasManageDestination = Boolean(
    snapshot?.managementUrl || display.managementFallbackUrl,
  );
  const canCancel = canStartCancellation(snapshot) && hasManageDestination;
  const profileUrl = user?.username
    ? buildPublicProfileUrl(user.username, 'individual')
    : null;

  const renewalLine = !paid
    ? null
    : !date
      ? t('subscription.billing.accessUnknown')
      : renewalOff
        ? t('subscription.billing.accessUntil', { date })
        : t('subscription.billing.renews', { date });

  const openUpgrade = () => {
    trackProductEvent('billing_change_plan_clicked', { tier, surface: 'web' });
    upgradeModal.open('individual');
  };

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await syncSubscription({ planSet: 'individual', companyId: null });
      toast.success(t('settings.subscriptionSynced'));
    } catch (error) {
      console.error('[SubscriptionBillingPanel] subscription sync failed', error);
      toast.error(t('settings.syncFailed'), t('settings.syncFailedMessage'));
    } finally {
      setRefreshing(false);
    }
  };

  const navigate = (href: string) => {
    closeActiveDialog();
    router.push(href as never);
  };

  if (!snapshot) {
    return (
      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
        {isLoading ? t('settings.checkingSubscription') : t('settings.billingUnavailable')}
      </p>
    );
  }

  const nextPaymentLabel = renewalOff
    ? t('subscription.billing.accessUntilLabel')
    : t('subscription.billing.nextPayment');

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="tabbio-sub" style={{ gap: 0 }}>
      <section className="tabbio-sub-summary" aria-label={t('subscription.billing.title')}>
        <div className="tabbio-sub-summary-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="tabbio-sub-summary-name">
              {paid ? (
                <span className="tabbio-sub-mark">
                  <PremiumMark size={22} color="currentColor" />
                </span>
              ) : null}
              {paid ? TIER_NAMES[tier] : t('subscription.tierFree')}
            </span>
            <p className="tabbio-sub-subtitle" style={{ marginTop: 4 }}>
              {t(`subscription.billing.tagline.${paid ? tier : 'free'}`)}
            </p>
          </div>
          <NovaMood mood={paid ? activity.mood : 'calm'} size={72} />
        </div>
        {paid ? (
          <div className="tabbio-sub-summary-meta">
            <span dir="auto">
              {priceDisplay
                ? t(
                    cycle === 'yearly'
                      ? 'subscription.billing.pricePerYear'
                      : 'subscription.billing.pricePerMonth',
                    { price: priceDisplay },
                  )
                : null}
            </span>
            <span>{renewalLine}</span>
          </div>
        ) : null}
      </section>

      {activity.metrics.length > 0 ? (
        <section
          className="tabbio-sub-metrics"
          aria-label={t('subscription.billing.metrics.label')}
          style={{ gridTemplateColumns: `repeat(${activity.metrics.length}, minmax(0, 1fr))` }}
        >
          {activity.metrics.map((metric) => (
            <div key={metric.key}>
              <strong>{formatActivityCount(metric.value, locale)}</strong>
              <span>{t(metric.labelKey)}</span>
            </div>
          ))}
        </section>
      ) : null}

      <div>
        <button
          type="button"
          className="tabbio-sub-action-row"
          onClick={() => navigate(IMPROVE_CV_HREF)}
        >
          <CvGlyph />
          <span style={{ flex: 1, minWidth: 0 }}>
            <strong>{t('subscription.billing.improveCv')}</strong>
            <small>{t('subscription.billing.improveCvSub')}</small>
          </span>
          <Icon name="chevron_right" size={20} flip />
        </button>
        <button
          type="button"
          className="tabbio-sub-action-row"
          onClick={() => {
            if (profileUrl) {
              // The owner marker keeps this visit out of their view count.
              window.open(withOwnerViewMarker(profileUrl), '_blank', 'noopener,noreferrer');
              return;
            }
            navigate('/(app)/profile');
          }}
        >
          <span className="tabbio-sub-mark">
            <MaterialSymbol name="link" size={24} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <strong>{t('subscription.billing.viewProfile')}</strong>
            <small>{t('subscription.billing.viewProfileSub')}</small>
          </span>
          <Icon name="chevron_right" size={20} flip />
        </button>
      </div>

      <SectionTitle>{t('subscription.billing.planDetails')}</SectionTitle>
      <SubGroup>
        <SubRow
          icon="credit_card"
          title={paid ? TIER_NAMES[tier] : t('subscription.tierFree')}
          sub={
            paid && priceDisplay
              ? t(
                  cycle === 'yearly'
                    ? 'subscription.billing.planPricePerYear'
                    : 'subscription.billing.planPricePerMonth',
                  { price: priceDisplay },
                )
              : undefined
          }
          value={t('subscription.billing.change')}
          onClick={openUpgrade}
        />
        {paid ? (
          <SubRow
            icon="calendar_month"
            title={nextPaymentLabel}
            sub={
              date
                ? !renewalOff && priceDisplay
                  ? t('subscription.billing.dateAndPrice', { date, price: priceDisplay })
                  : date
                : t('subscription.billing.accessUnknown')
            }
            onClick={() => setInfo('nextPayment')}
          />
        ) : null}
        {paid && hasManageDestination ? (
          <SubRow
            icon="tune"
            title={t('subscription.billing.manageSubscription')}
            sub={provider}
            onClick={() => setJourney({ step: 'provider', intent: 'manage' })}
          />
        ) : null}
        {renewalOff && hasManageDestination ? (
          <SubRow
            icon="autorenew"
            title={t('subscription.billing.turnRenewalOn')}
            sub={t('subscription.billing.renewalOff')}
            onClick={() => setJourney({ step: 'provider', intent: 'renew' })}
          />
        ) : canCancel ? (
          <SubRow
            icon="close"
            danger
            title={t('subscription.billing.cancelSubscription')}
            onClick={() => setJourney({ step: 'reason' })}
          />
        ) : null}
      </SubGroup>

      <SectionTitle>{t('subscription.billing.billingHelp')}</SectionTitle>
      <SubGroup>
        {paid ? (
          <SubRow
            icon="receipt_long"
            title={t('subscription.billing.receipts')}
            onClick={() => setInfo('receipts')}
          />
        ) : null}
        <SubRow
          icon="sync"
          title={
            storeOwned && paid
              ? t('subscription.billing.restorePurchases')
              : t('subscription.billing.refreshSubscription')
          }
          onClick={refreshing ? undefined : () => void refresh()}
          disabled={refreshing}
        />
        {paid ? (
          <SubRow
            icon="help"
            title={t('subscription.billing.requestRefund')}
            sub={provider}
            onClick={() => setInfo('refund')}
          />
        ) : null}
      </SubGroup>

      {info === 'nextPayment' ? (
        <InfoDialog
          title={
            renewalOff
              ? t('subscription.billing.accessUntilLabel')
              : t('subscription.info.nextPayment.title')
          }
          body={
            date
              ? !renewalOff && priceDisplay
                ? t('subscription.billing.dateAndPrice', { date, price: priceDisplay })
                : date
              : t('subscription.billing.accessUnknown')
          }
          note={t('subscription.info.nextPayment.body')}
          onClose={() => setInfo(null)}
        />
      ) : null}
      {info === 'receipts' ? (
        <InfoDialog
          title={t('subscription.info.receipts.title')}
          body={t('subscription.info.receipts.body', { provider })}
          onClose={() => setInfo(null)}
        />
      ) : null}
      {info === 'refund' ? (
        <InfoDialog
          title={t('subscription.info.refund.title')}
          body={t('subscription.info.refund.body', { provider })}
          note={t('subscription.info.refund.note')}
          onClose={() => setInfo(null)}
        />
      ) : null}
      <CancelPlanDialog
        key={journey ? JSON.stringify(journey) : 'closed'}
        open={Boolean(journey)}
        entry={journey ?? undefined}
        onClose={() => setJourney(null)}
      />
    </div>
  );
}
